import React, { createContext, FC, useCallback, useMemo, useState } from "react";

import { useMe } from "modules/User/hooks/useMe";
import { ContextProps } from "./types";
import { usersClient } from "modules/User/client/usersClient";
import { useQueryClient } from "react-query";
import { cacheKeys } from "config";
import { setAuthToken } from "shared/clients/baseClient";

export const defaultContext: ContextProps = {
  user: undefined,
  login: () => {},
  ssoLogin: () => {},
  logout: () => {},
  isLoggedIn: false,
  isLoading: false,
  isLoadingUser: false,
  error: undefined,
};

export const UserContext = createContext(defaultContext);
export const UserContextProvider: FC<React.PropsWithChildren> = ({ children }) => {

  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { user, isLoading: isLoadingUser } = useMe();

  const isLoggedIn = useMemo(() => !!user, [user]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const userToken = await usersClient.loginWithEmailAndPassword({ username, password });
      setAuthToken(userToken.data.access_token);
      queryClient.invalidateQueries(cacheKeys.users.getMe);
    }
    catch (error: any) {
      console.error(error);
      setError(error);
    }
    finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  return (
    <UserContext.Provider
      value={{
        user,
        logout: () => {},
        login,
        ssoLogin: () => {},
        isLoggedIn,
        isLoading,
        isLoadingUser,
        error,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
